import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { PersonTasksResult, PersonTask } from '../../lib/peopleApi';
import type { TaskPriority } from '../../lib/tasksApi';
import { personTaskRow, TaskListRow } from '../tasks/TaskListRow';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { matchesDueBucket, priorityLabel, type DueBucket } from './personTaskUtils';

interface Props {
  data: PersonTasksResult;
  /** The person whose tasks these are — drawn as the row's avatar. */
  assigneeName?: string | null;
}

const PRIORITY_FILTERS: TaskPriority[] = ['URGENT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'];

const DUE_OPTIONS: { value: DueBucket; label: string }[] = [
  { value: 'overdue', label: 'Întârziate' },
  { value: 'today', label: 'Astăzi' },
  { value: 'this_week', label: 'Săptămâna aceasta' },
  { value: 'this_month', label: 'Luna aceasta' },
  { value: 'none', label: 'Fără termen limită' },
  { value: 'custom', label: 'Interval personalizat' },
];

const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  URGENT: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
  NONE: 0,
};

function toggle<T>(set: T[], value: T): T[] {
  return set.includes(value) ? set.filter((v) => v !== value) : [...set, value];
}

function Chip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // nowrap: on a phone "În lucru" broke inside the pill and showed as "În".
      className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition ${
        active
          ? `border-transparent ${className ?? 'bg-brand-600 text-white'}`
          : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The shared task row plus this list's one extra: the task's labels, as a
 * line underneath (the row itself has no label slot, and labels are a filter
 * here, so seeing them on the row matters).
 */
function PersonTaskRow({ task, assigneeName }: { task: PersonTask; assigneeName: string | null }) {
  return (
    <TaskListRow task={personTaskRow(task, assigneeName)}>
      {task.labels.length > 0 && (
        <span className="flex flex-wrap gap-1">
          {task.labels.map((l) => (
            <span
              key={l.id}
              className="rounded px-1.5 py-px text-[11px] font-medium text-gray-700"
              style={{ backgroundColor: l.color ? `${l.color}22` : '#f3f4f6' }}
            >
              {l.name}
            </span>
          ))}
        </span>
      )}
    </TaskListRow>
  );
}

/**
 * Full task list for a person (CLE-155) with instant client-side search
 * and filters (due date, status, priority, project, labels), optional grouping
 * by project, and a default priority-desc / due-asc sort. Rows are the shared
 * Linear-style task row (CLE-435: clicking opens the task detail page).
 */
export function PersonTasksSection({ data, assigneeName = null }: Props) {
  const { statuses } = useTaskStatuses();
  const [search, setSearch] = useState('');

  // Status filter holds status slugs (configurable statuses).
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority[]>([]);
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [dueFilter, setDueFilter] = useState<DueBucket | ''>('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [groupByProject, setGroupByProject] = useState(false);

  // Unique projects present in the task set, for the project dropdown.
  const projects = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of data.tasks) {
      const key = t.projectId ?? t.projectName ?? 'Unassigned';
      map.set(key, t.projectName ?? 'Nealocat');
    }
    return [...map.entries()].map(([key, name]) => ({ key, name }));
  }, [data.tasks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    const prioritySet = new Set(priorityFilter);
    const labelSet = new Set(labelFilter.map((l) => l.toLowerCase()));

    const out = data.tasks.filter((t) => {
      if (q) {
        const hay = `${t.title} ${t.identifier ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusSet.size && !statusSet.has(t.status)) return false;
      if (prioritySet.size && !prioritySet.has(t.priority)) return false;
      if (projectFilter) {
        const key = t.projectId ?? t.projectName ?? 'Unassigned';
        if (key !== projectFilter) return false;
      }
      if (labelSet.size) {
        const names = t.labels.map((l) => l.name.toLowerCase());
        if (!names.some((n) => labelSet.has(n))) return false;
      }
      if (!matchesDueBucket(t, dueFilter, { from: customFrom, to: customTo })) return false;
      return true;
    });

    out.sort((a, b) => {
      const pd = PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
      if (pd !== 0) return pd;
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
      return ad - bd;
    });
    return out;
  }, [
    data.tasks,
    search,
    statusFilter,
    priorityFilter,
    projectFilter,
    labelFilter,
    dueFilter,
    customFrom,
    customTo,
  ]);

  const grouped = useMemo(() => {
    if (!groupByProject) return null;
    const map = new Map<string, PersonTask[]>();
    for (const t of filtered) {
      const name = t.projectName ?? 'Nealocat';
      const arr = map.get(name) ?? [];
      arr.push(t);
      map.set(name, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupByProject]);

  const hasActiveFilters =
    Boolean(search) ||
    statusFilter.length > 0 ||
    priorityFilter.length > 0 ||
    Boolean(projectFilter) ||
    labelFilter.length > 0 ||
    Boolean(dueFilter);

  const clearAll = () => {
    setSearch('');
    setStatusFilter([]);
    setPriorityFilter([]);
    setProjectFilter('');
    setLabelFilter([]);
    setDueFilter('');
    setCustomFrom('');
    setCustomTo('');
  };

  return (
    <div className="space-y-4">
      {/* Search + project dropdown + group toggle. Phones: search across the
          row, the two dropdowns as equal halves, the toggle on its own line. */}
      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap sm:gap-3">
        <div className="relative col-span-2 sm:min-w-[200px] sm:flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după titlu sau identificator…"
            className="ui-input pl-8 pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Șterge căutarea"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select
          label="Proiect"
          hideLabel
          size="sm"
          clearable
          value={projectFilter}
          onChange={setProjectFilter}
          placeholder="Toate proiectele"
          options={projects.map((p) => ({ value: p.key, label: p.name }))}
          className="w-full sm:w-48"
        />

        <Select
          label="Termen limită"
          hideLabel
          size="sm"
          clearable
          value={dueFilter}
          onChange={(v) => setDueFilter(v as DueBucket | '')}
          placeholder="Orice termen limită"
          options={DUE_OPTIONS}
          className="w-full sm:w-52"
        />

        <label className="col-span-2 flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={groupByProject}
            onChange={(e) => setGroupByProject(e.target.checked)}
            className="rounded border-gray-300"
          />
          Grupează după proiect
        </label>
      </div>

      {dueFilter === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <span>De la</span>
          <DateField value={customFrom} onChange={setCustomFrom} className="w-40" />
          <span>până la</span>
          <DateField value={customTo} onChange={setCustomTo} className="w-40" />
        </div>
      )}

      {/* Status chips. Phones: the group title on its own line, the pills
          under it (beside it they started mid-row and wrapped unevenly). */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 basis-full text-xs font-medium uppercase tracking-wide text-gray-500 sm:basis-auto">
          Status
        </span>
        {statuses.map((s) => (
          <Chip
            key={s.id}
            active={statusFilter.includes(s.slug)}
            onClick={() => setStatusFilter((prev) => toggle(prev, s.slug))}
          >
            {s.name}
            {data.stats.byStatus[s.slug] ? ` (${data.stats.byStatus[s.slug]})` : ''}
          </Chip>
        ))}
      </div>

      {/* Priority chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 basis-full text-xs font-medium uppercase tracking-wide text-gray-500 sm:basis-auto">
          Prioritate
        </span>
        {PRIORITY_FILTERS.map((p) => (
          <Chip
            key={p}
            active={priorityFilter.includes(p)}
            onClick={() => setPriorityFilter((prev) => toggle(prev, p))}
          >
            {priorityLabel(p)}
            {data.stats.byPriority[p] ? ` (${data.stats.byPriority[p]})` : ''}
          </Chip>
        ))}
      </div>

      {/* Label chips (only when present) */}
      {data.labels.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 basis-full text-xs font-medium uppercase tracking-wide text-gray-500 sm:basis-auto">
            Etichete
          </span>
          {data.labels.map((l) => (
            <Chip
              key={l.id}
              active={labelFilter.includes(l.name)}
              onClick={() => setLabelFilter((prev) => toggle(prev, l.name))}
            >
              {l.name}
            </Chip>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Se afișează {filtered.length} din {data.tasks.length} sarcini
        </span>
        {hasActiveFilters && (
          <button type="button" onClick={clearAll} className="text-brand-600 hover:underline">
            Șterge filtrele
          </button>
        )}
      </div>

      {/* Task rows */}
      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          Nicio sarcină nu corespunde filtrelor.
        </p>
      ) : grouped ? (
        <div className="space-y-3">
          {grouped.map(([projectName, tasks]) => (
            <div key={`group-${projectName}`}>
              <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {projectName} · {tasks.length}
              </p>
              <ul className="mt-1 overflow-hidden rounded-md border border-gray-200">
                {tasks.map((t) => (
                  <PersonTaskRow key={t.id} task={t} assigneeName={assigneeName} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="overflow-hidden rounded-md border border-gray-200">
          {filtered.map((t) => (
            <PersonTaskRow key={t.id} task={t} assigneeName={assigneeName} />
          ))}
        </ul>
      )}
    </div>
  );
}
